import BriefcaseIcon from 'assets/icons/briefcase-icon.svg'
import CaretDown from 'assets/icons/caret-down-grey.svg?component'
import CashStackIcon from 'assets/icons/cash-stack-icon.svg?component'
import ClockIcon from 'assets/icons/clock-icon.svg?component'
import PercentageIcon from 'assets/icons/percentage-icon.svg?component'
import StarIcon from 'assets/icons/star-icon.svg?component'
import ConditionBlock, { ConditionProps } from 'components/UI/Blocks/ConditionBlock'
import Typography from 'components/UI/Typography'
import { useAppSelector } from 'hooks'
import { Fragment, useMemo, useState } from 'react'
import { Collapse } from 'react-collapse'
import { useTranslation } from 'react-i18next'
import { getCreditParamsSelector, getStatusSelector } from 'redux/store/slices/statusSlice'
import tw from 'twin.macro'
import { numberWithSpaces } from 'utils'
import { getProgramName, getRepaymentMethodName } from 'utils/translations'

const styles = tw`max-w-[191px] w-full md:first-of-type:pl-3 lg:first-of-type:pl-0 border-l-[1px] border-stroke first-of-type:border-0`

const LoanConditionLine = () => {
  const { t } = useTranslation()

  const { interest_rate, loan_amount, period } = useAppSelector(getCreditParamsSelector)
  const {
    credit_program,
    credit_params: { repayment_method }
  } = useAppSelector(getStatusSelector)

  const [isCondOpen, setIsCondOpen] = useState(false)

  const conditionsMobile = useMemo(
    () => [
      [
        {
          caption: t('loan-conditions.loan-amount', { ns: 'common' }),
          body: `${numberWithSpaces(Number(loan_amount).toFixed(0))} ₸`,
          icon: <CashStackIcon />
        },
        {
          caption: t('loan.period', { ns: 'common' }),
          body: t('calculator.period-limit', { ns: 'common', period: period }),
          icon: <ClockIcon />
        },
        {
          caption: t('loan.interest-rate', { ns: 'common' }),
          body: `${interest_rate}%`
        }
      ],
      [
        {
          caption: t('programs.title', { ns: 'common' }),
          body: t(getProgramName(credit_program), { ns: 'common' }),
          icon: <StarIcon />
        },

        {
          caption: t('loan.interest-rate', { ns: 'common' }),
          body: t(getRepaymentMethodName(repayment_method), { ns: 'common' }),
          icon: <BriefcaseIcon />
        }
      ]
    ],
    [credit_program, interest_rate, loan_amount, period, repayment_method, t]
  )

  const loanConditions = useMemo<Array<ConditionProps>>(
    () => [
      {
        caption: t('loan-conditions.loan-amount', { ns: 'common' }),
        body: `${numberWithSpaces(Number(loan_amount).toFixed(0))} ₸`,
        icon: <CashStackIcon tw='flex-shrink-0' />
      },
      {
        caption: t('loan.interest-rate', { ns: 'common' }),
        body: `${interest_rate}%`,
        icon: <PercentageIcon tw='flex-shrink-0' />
      },
      {
        caption: t('programs.title', { ns: 'common' }),
        body: t(getProgramName(credit_program), { ns: 'common' }),
        icon: <StarIcon tw='flex-shrink-0' />
      },
      {
        caption: t('loan.period', { ns: 'common' }),
        body: t('calculator.period-limit', { ns: 'common', period: period }),
        icon: <ClockIcon tw='flex-shrink-0' />
      },
      {
        caption: t('loan.repayment-method', { ns: 'common' }),
        body: t(getRepaymentMethodName(repayment_method), { ns: 'common' }),
        icon: <ClockIcon tw='flex-shrink-0' />
      }
    ],
    [t, loan_amount, interest_rate, credit_program, period, repayment_method]
  )

  return (
    <Fragment>
      <div tw='hidden md:flex justify-center w-full  left-0 top-[84px] border-t bg-secondary border-stroke z-10 '>
        {loanConditions.map((item, id) => {
          return (
            <ConditionBlock
              twStyle={styles}
              key={`condition_${id}`}
              body={item.body}
              caption={item.caption}
              icon={item.icon}
            />
          )
        })}
      </div>
      <div className='flex bg-grey-middle md:hidden py-3 px-5'>
        <div className='flex-1 space-y-4'>
          <div css={[tw`grid grid-cols-3 gap-x-4`]}>
            {conditionsMobile[0].map((item, id) => {
              return (
                <div key={id} tw='space-y-[0.125em]'>
                  <Typography text twStyle={tw`text-[12px] leading-[14px] tracking-[0.0025em] text-grey-dark `}>
                    {item.caption}
                  </Typography>
                  <Typography text twStyle={tw`text-base leading-4`} weight='bold'>
                    {item.body}
                  </Typography>
                </div>
              )
            })}
          </div>
          <Collapse isOpened={isCondOpen}>
            <div css={[tw`grid grid-cols-3 gap-x-4`]}>
              {conditionsMobile[1].map((item, id) => {
                return (
                  <div key={id} tw='space-y-[0.125em]'>
                    <Typography text twStyle={tw`text-[12px] leading-[14px] tracking-[0.0025em] text-grey-dark `}>
                      {item.caption}
                    </Typography>
                    <Typography text twStyle={tw`text-base leading-4 `} weight='bold'>
                      {item.body}
                    </Typography>
                  </div>
                )
              })}
            </div>
          </Collapse>
        </div>
        <div className='pt-1'>
          <CaretDown
            css={[tw`flex-shrink-0 duration-300 ml-auto`, isCondOpen && tw`rotate-180 `]}
            onClick={() => setIsCondOpen(prev => !prev)}
          />
        </div>
      </div>
    </Fragment>
  )
}

export default LoanConditionLine
